(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  document.querySelectorAll('.sw-gallery').forEach(initGallery);

  // ─── Gallery instance ──────────────────────────────────────────────────────

  function initGallery(root) {
    const items        = Array.from(root.querySelectorAll('.sw-gallery__item'));
    const chips        = Array.from(root.querySelectorAll('.sw-gallery__chip'));
    const moreWrap     = root.querySelector('.sw-gallery__more-wrap');
    const moreBtn      = root.querySelector('.sw-gallery__more');
    const initialCount = parseInt(root.dataset.initial || '24', 10);
    const filterMode   = root.dataset.filterMode || 'or';
    const useLightbox  = root.dataset.lightbox === 'true';
    const animStyle    = root.dataset.anim || 'fade';

    let activeTags   = new Set();
    let visibleCount = initialCount;

    // ── Tag helpers ──────────────────────────────────────────────────────────

    const itemTags = (item) =>
      (item.dataset.tags || '').split(',').map((s) => s.trim()).filter(Boolean);

    const matchesFilter = (item) => {
      if (activeTags.size === 0) return true;
      const tags = itemTags(item);
      return filterMode === 'and'
        ? [...activeTags].every((t) => tags.includes(t))
        : [...activeTags].some((t) => tags.includes(t));
    };

    // ── Stagger entrance via IntersectionObserver ────────────────────────────

    if (reducedMotion) {
      items.forEach((it) => it.classList.add('is-visible'));
    } else {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              io.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.05 }
      );
      items.forEach((it) => io.observe(it));
    }

    // ── Filter + FLIP ────────────────────────────────────────────────────────

    const ENTER_FRAMES = {
      fade:  [{ opacity: 0 },                              { opacity: 1 }],
      scale: [{ opacity: 0, transform: 'scale(0.82)' },    { opacity: 1, transform: 'scale(1)' }],
      flip:  [{ opacity: 0, transform: 'rotateY(-90deg)' },{ opacity: 1, transform: 'rotateY(0deg)' }],
    };

    const applyFilter = (skipAnim = false) => {
      const matching = items.filter(matchesFilter);
      const showing  = new Set(matching.slice(0, visibleCount));

      if (moreWrap) moreWrap.hidden = matching.length <= visibleCount;

      if (reducedMotion || skipAnim) {
        items.forEach((it) => it.classList.toggle('is-hidden', !showing.has(it)));
        return;
      }

      // FLIP — First: record rects of all currently visible items
      const wasHidden   = new Set(items.filter((it) => it.classList.contains('is-hidden')));
      const firstRects  = new Map();
      items.forEach((it) => {
        if (!wasHidden.has(it)) firstRects.set(it, it.getBoundingClientRect());
      });

      // Apply DOM changes
      items.forEach((it) => it.classList.toggle('is-hidden', !showing.has(it)));

      // FLIP — Play: animate repositioned items
      showing.forEach((it) => {
        if (wasHidden.has(it)) return; // newly entering — handled below
        const first = firstRects.get(it);
        if (!first) return;
        const last = it.getBoundingClientRect();
        const dx   = first.left - last.left;
        const dy   = first.top  - last.top;
        if (!dx && !dy) return;
        it.animate(
          [{ transform: `translate(${dx}px,${dy}px)` }, { transform: 'none' }],
          { duration: 360, easing: 'cubic-bezier(0.2,0.7,0.2,1)' }
        );
      });

      // Enter animation for newly revealed items
      const enterFrames = ENTER_FRAMES[animStyle] || ENTER_FRAMES.fade;
      let staggerIdx = 0;
      showing.forEach((it) => {
        if (!wasHidden.has(it)) return;
        it.animate(enterFrames, {
          duration: 340,
          delay:    Math.min(staggerIdx * 45, 240),
          easing:   'ease-out',
          fill:     'both',
        });
        staggerIdx++;
      });
    };

    // ── Chip interaction ─────────────────────────────────────────────────────

    const allChip = chips.find((c) => c.dataset.filter === 'all');

    chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        const f = chip.dataset.filter;

        if (f === 'all') {
          activeTags.clear();
          chips.forEach((c) => c.classList.toggle('is-active', c.dataset.filter === 'all'));
        } else {
          activeTags.has(f) ? activeTags.delete(f) : activeTags.add(f);
          chip.classList.toggle('is-active', activeTags.has(f));
          if (allChip) allChip.classList.toggle('is-active', activeTags.size === 0);
        }

        visibleCount = initialCount;
        applyFilter();
        syncQueryParams();
      });
    });

    // ── Load more ────────────────────────────────────────────────────────────

    if (moreBtn) {
      moreBtn.addEventListener('click', () => {
        visibleCount += initialCount;
        applyFilter(true);
      });
    }

    // ── URL deep-link ────────────────────────────────────────────────────────

    const syncQueryParams = () => {
      const url = new URL(window.location.href);
      if (activeTags.size > 0) {
        url.searchParams.set('tags', [...activeTags].join(','));
      } else {
        url.searchParams.delete('tags');
      }
      history.replaceState(null, '', url);
    };

    const restoreFromQueryParams = () => {
      const params = new URL(window.location.href).searchParams.get('tags');
      if (!params) return;
      params.split(',').map((s) => s.trim()).filter(Boolean).forEach((t) => activeTags.add(t));
      chips.forEach((c) => {
        if (c.dataset.filter === 'all') {
          c.classList.toggle('is-active', activeTags.size === 0);
        } else {
          c.classList.toggle('is-active', activeTags.has(c.dataset.filter));
        }
      });
    };

    // ── Init ─────────────────────────────────────────────────────────────────

    restoreFromQueryParams();
    applyFilter(true);

    if (useLightbox) {
      initLightbox(root, items, () => items.filter((it) => !it.classList.contains('is-hidden')));
    }
  }

  // ─── Lightbox ──────────────────────────────────────────────────────────────

  function initLightbox(root, items, getVisible) {
    const lb       = root.querySelector('.sw-gallery__lightbox');
    if (!lb) return;

    const img      = lb.querySelector('.sw-gallery__lb-img');
    const capEl    = lb.querySelector('.sw-gallery__lb-caption');
    const btnClose = lb.querySelector('.sw-gallery__lb-close');
    const btnPrev  = lb.querySelector('.sw-gallery__lb-prev');
    const btnNext  = lb.querySelector('.sw-gallery__lb-next');
    const zoomOn   = root.dataset.zoom === 'true';

    let order      = [];
    let pos        = 0;
    let lastOpener = null;

    // ── Zoom state ───────────────────────────────────────────────────────────

    let zoom = 1, panX = 0, panY = 0;

    const resetZoom = () => {
      zoom = 1; panX = 0; panY = 0;
      img.style.transform = '';
      img.classList.remove('is-zoomed');
      if (zoomOn) img.style.cursor = 'zoom-in';
    };

    const applyTransform = () => {
      img.style.transform = `translate(${panX}px,${panY}px) scale(${zoom})`;
    };

    const clampPan = () => {
      if (zoom <= 1) { panX = 0; panY = 0; return; }
      const stage = lb.querySelector('.sw-gallery__lb-stage');
      const sr    = stage.getBoundingClientRect();
      const ir    = img.getBoundingClientRect();
      const maxX  = Math.max(0, (ir.width  - sr.width)  / 2);
      const maxY  = Math.max(0, (ir.height - sr.height) / 2);
      panX = Math.max(-maxX, Math.min(maxX, panX));
      panY = Math.max(-maxY, Math.min(maxY, panY));
    };

    // ── Render ───────────────────────────────────────────────────────────────

    const render = (idx) => {
      pos = ((idx % order.length) + order.length) % order.length;
      const it    = order[pos];
      const hdEl  = it.querySelector('.sw-gallery__hd');
      const src   = hdEl ? hdEl.dataset.src : (it.querySelector('img') || {}).src || '';
      const cap   = hdEl ? (hdEl.dataset.caption || '') : '';

      img.src = src;
      img.alt = cap;
      capEl.textContent = cap;
      resetZoom();

      btnPrev.hidden = order.length <= 1;
      btnNext.hidden = order.length <= 1;
    };

    // ── Open / close ─────────────────────────────────────────────────────────

    const open = (item) => {
      order      = getVisible();
      const idx  = order.indexOf(item);
      lastOpener = item.querySelector('.sw-gallery__open');

      render(idx >= 0 ? idx : 0);

      lb.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';
      btnClose.focus();

      // Fade in
      lb.style.opacity = '0';
      requestAnimationFrame(() => {
        lb.style.transition = 'opacity 0.22s ease';
        lb.style.opacity = '1';
      });
    };

    const close = () => {
      lb.style.opacity = '0';
      setTimeout(() => {
        lb.setAttribute('hidden', '');
        lb.style.opacity    = '';
        lb.style.transition = '';
        document.body.style.overflow = '';
        if (lastOpener) lastOpener.focus();
      }, 220);
    };

    const go = (delta) => render(pos + delta);

    // ── Thumbnail click ───────────────────────────────────────────────────────

    items.forEach((it) => {
      const opener = it.querySelector('.sw-gallery__open');
      if (!opener) return;
      opener.addEventListener('click', (e) => { e.preventDefault(); open(it); });
    });

    // ── Controls ─────────────────────────────────────────────────────────────

    btnClose.addEventListener('click', close);
    btnPrev.addEventListener('click',  () => go(-1));
    btnNext.addEventListener('click',  () => go(1));

    lb.addEventListener('click', (e) => { if (e.target === lb) close(); });

    // ── Keyboard ─────────────────────────────────────────────────────────────

    document.addEventListener('keydown', (e) => {
      if (lb.hasAttribute('hidden')) return;
      if (e.key === 'Escape')     { e.preventDefault(); close(); }
      if (e.key === 'ArrowLeft')  { e.preventDefault(); go(-1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); go(1);  }
    });

    // Focus trap
    lb.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      const focusable = Array.from(lb.querySelectorAll('button')).filter((el) => !el.hidden);
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];
      if (!first) return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    // ── Zoom + pan ────────────────────────────────────────────────────────────

    if (!zoomOn) return;

    const setZoom = (newZoom) => {
      zoom = Math.min(5, Math.max(1, newZoom));
      if (zoom <= 1) { zoom = 1; panX = 0; panY = 0; }
      img.classList.toggle('is-zoomed', zoom > 1);
      img.style.cursor = zoom > 1 ? 'grab' : 'zoom-in';
      clampPan();
      applyTransform();
    };

    // Click → toggle
    img.addEventListener('click', (e) => {
      e.stopPropagation();
      setZoom(zoom > 1 ? 1 : 2.5);
    });

    // Wheel → smooth zoom
    img.addEventListener('wheel', (e) => {
      e.preventDefault();
      setZoom(zoom + (e.deltaY < 0 ? 0.3 : -0.3));
    }, { passive: false });

    // ── Drag / pan ────────────────────────────────────────────────────────────

    let dragging = false, dragStartX = 0, dragStartY = 0, originPanX = 0, originPanY = 0;

    img.addEventListener('pointerdown', (e) => {
      if (zoom <= 1) return;
      dragging    = true;
      dragStartX  = e.clientX; dragStartY  = e.clientY;
      originPanX  = panX;      originPanY  = panY;
      img.setPointerCapture(e.pointerId);
      img.classList.add('is-dragging');
    });

    img.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      panX = originPanX + (e.clientX - dragStartX);
      panY = originPanY + (e.clientY - dragStartY);
      clampPan();
      applyTransform();
    });

    const endDrag = () => {
      if (!dragging) return;
      dragging = false;
      img.classList.remove('is-dragging');
      img.style.cursor = zoom > 1 ? 'grab' : 'zoom-in';
    };

    img.addEventListener('pointerup',     endDrag);
    img.addEventListener('pointercancel', endDrag);

    // ── Pinch-to-zoom ─────────────────────────────────────────────────────────

    const ptrs       = new Map();
    let pinchDist0   = 0;
    let pinchZoom0   = 1;

    const ptrDist = () => {
      const [a, b] = [...ptrs.values()];
      return Math.hypot(a.x - b.x, a.y - b.y);
    };

    img.addEventListener('pointerdown', (e) => {
      ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
    });

    img.addEventListener('pointermove', (e) => {
      if (!ptrs.has(e.pointerId)) return;
      ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (ptrs.size < 2) return;
      const dist = ptrDist();
      if (!pinchDist0) { pinchDist0 = dist; pinchZoom0 = zoom; return; }
      setZoom(pinchZoom0 * (dist / pinchDist0));
    });

    const clearPtr = (e) => {
      ptrs.delete(e.pointerId);
      if (ptrs.size < 2) pinchDist0 = 0;
    };
    img.addEventListener('pointerup',     clearPtr);
    img.addEventListener('pointercancel', clearPtr);
  }
})();
